import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AutoBalanceTransfer, Box, CardTabs, DefaultBankAccountList, SupportedCurrencies } from 'vopay';
import { useAlertDialog, useBlockingLoader, useToast } from 'shared';
import { BodyText, Skeleton, Title } from 'UI';
import MessageBox from '@/components/UI/MessageBox/MessageBox';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useAutoBalanceTransfer from './useAutoBalanceTransfer';
import useFetchPayoutAccount from './useFetchPayoutAccount';
import NoTransferCard from './NoTransferCard/NoTransferCard';
import TransferOverview from './TransferOverview/TransferOverview';

type CurrencyData = {
  operationalBankAccount?: DefaultBankAccountList;
  transfer?: AutoBalanceTransfer;
  isTransferUnknown: boolean;
};

type CurrencyTabsProps = {
  currencies: SupportedCurrencies[];
  activeCurrency?: SupportedCurrencies;
  onActiveCurrencyChange: (currency?: SupportedCurrencies) => void;
  renderCurrency: (currency: SupportedCurrencies, isCurrencyNamed: boolean) => ReactElement;
};

function CurrencyTabs({ currencies, activeCurrency, onActiveCurrencyChange, renderCurrency }: CurrencyTabsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.AutoBalanceTransferPage' });

  if (currencies.length === 1) return renderCurrency(currencies[0], false);

  return (
    <CardTabs.Root
      value={currencies.find((currency) => currency === activeCurrency) ?? currencies[0]}
      onValueChange={(value) => onActiveCurrencyChange(currencies.find((currency) => currency === value))}
    >
      <CardTabs.List aria-label={t('currencyTabs', { defaultValue: 'Currencies' })}>
        {currencies.map((currency) => (
          <CardTabs.Trigger key={currency} value={currency}>
            {currency}
          </CardTabs.Trigger>
        ))}
      </CardTabs.List>

      {currencies.map((currency) => (
        <CardTabs.Content key={currency} value={currency}>
          {renderCurrency(currency, true)}
        </CardTabs.Content>
      ))}
    </CardTabs.Root>
  );
}

export default function AutoBalanceTransferPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.AutoBalanceTransferPage' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  // Set Up and Update hand back the currency they wrote, so the page opens on its tab.
  const { state } = useLocation<{ currency?: SupportedCurrencies } | undefined>();
  const { permissions } = usePermissions();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();

  const {
    response: { Country, clientAccountsEnabled },
  } = useClientData();

  const { balances, hasFetchedBalances } = useAccountBalances();
  const { fetchPayoutAccount } = useFetchPayoutAccount();
  const { fetchAutoBalanceTransfer, cancelAutoBalanceTransfer } = useAutoBalanceTransfer();
  const [currencyData, setCurrencyData] = useState<Partial<Record<SupportedCurrencies, CurrencyData>>>();
  const [activeCurrency, setActiveCurrency] = useState(state?.currency);
  // Hides a cancelled transfer without refetching, keeps its tab from vanishing when nothing else justified it, and
  // keeps the success notice up until the page is reloaded.
  const [cancelledCurrencies, setCancelledCurrencies] = useState<SupportedCurrencies[]>([]);

  const countryCurrency: SupportedCurrencies = Country === 'US' ? 'USD' : 'CAD';
  const balanceCurrencies = balances.map(({ Currency }) => Currency);
  // Transfers pay out over EFT or ACH only: Canadian accounts in CAD or USD, US accounts in USD. Balances in any other
  // currency can't be swept, so they never get a tab.
  const candidateCurrencies: SupportedCurrencies[] = countryCurrency === 'CAD' ? ['CAD', 'USD'] : ['USD'];
  const lastCancelledCurrency = cancelledCurrencies[cancelledCurrencies.length - 1];

  const getActiveTransfer = (currency: SupportedCurrencies) => {
    const transfer = currencyData?.[currency]?.transfer;

    return cancelledCurrencies.includes(currency) ? undefined : transfer;
  };

  // a tab per currency the account holds a balance in, or has an operational account or a transfer in. The
  // country currency is the floor if balances fail to load.
  const currencies = candidateCurrencies.filter(
    (currency) =>
      currency === countryCurrency ||
      balanceCurrencies.includes(currency) ||
      cancelledCurrencies.includes(currency) ||
      !!getActiveTransfer(currency) ||
      !!currencyData?.[currency]?.operationalBankAccount,
  );

  const cancelTransfer = async (currency: SupportedCurrencies, isCurrencyNamed: boolean) => {
    const result = await alertDialog.caution(
      isCurrencyNamed
        ? t('CancelDialog.titleCurrency', {
            defaultValue: 'Cancel this automated {{currency}} balance transfer?',
            currency,
          })
        : t('CancelDialog.title', { defaultValue: 'Cancel this automated balance transfer?' }),
      t('CancelDialog.description', {
        defaultValue: 'No further transfers will run. You can set a new one up at any time.',
      }),
      {
        abortLabel: t('CancelDialog.keepButton', { defaultValue: 'Keep Transfer' }),
        confirmLabel: t('CancelDialog.cancelButton', { defaultValue: 'Cancel Transfer' }),
      },
    );

    if (result !== 'confirm') return;

    blockingLoader.show();

    try {
      await cancelAutoBalanceTransfer({ Currency: currency });

      setCancelledCurrencies((previous) => [...previous, currency]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : tGlobal('genericError');

      toast.neutral(
        t('cancelError', {
          defaultValue: 'Could not cancel your auto balance transfer: {{errorMessage}}',
          errorMessage,
        }),
      );
    } finally {
      blockingLoader.hide();
    }
  };

  useEffect(() => {
    const loadCurrency = async (currency: SupportedCurrencies) => {
      const [operationalAccountResult, transferResult] = await Promise.allSettled([
        fetchPayoutAccount({ Currency: currency }),
        fetchAutoBalanceTransfer({ Currency: currency }),
      ]);

      if (operationalAccountResult.status === 'rejected') {
        const { reason } = operationalAccountResult;
        const errorMessage = reason instanceof Error ? reason.message : tGlobal('genericError');

        toast.neutral(
          t('fetchOperationalAccountError', {
            defaultValue: 'Could not load your operational bank account: {{errorMessage}}',
            errorMessage,
          }),
        );
      }

      if (transferResult.status === 'rejected') {
        const { reason } = transferResult;
        const errorMessage = reason instanceof Error ? reason.message : tGlobal('genericError');

        toast.neutral(
          t('fetchTransferError', {
            defaultValue: 'Could not load your auto balance transfer: {{errorMessage}}',
            errorMessage,
          }),
        );
      }

      const data: CurrencyData = {
        operationalBankAccount:
          operationalAccountResult.status === 'fulfilled' ? operationalAccountResult.value : undefined,
        transfer: transferResult.status === 'fulfilled' ? transferResult.value : undefined,
        isTransferUnknown: transferResult.status === 'rejected',
      };

      return [currency, data] as const;
    };

    const load = async () => {
      const results = await Promise.all(candidateCurrencies.map(loadCurrency));

      setCurrencyData(Object.fromEntries(results));
    };

    // Wait for the balances too, so a balance-only tab doesn't pop in after the page has rendered.
    if (hasFetchedBalances) load();
  }, [countryCurrency, hasFetchedBalances]);

  const renderCurrency = (currency: SupportedCurrencies, isCurrencyNamed: boolean) => {
    const activeTransfer = getActiveTransfer(currency);
    const hasOperationalAccount = !!currencyData?.[currency]?.operationalBankAccount;

    if (activeTransfer) {
      return (
        <TransferOverview
          transfer={activeTransfer}
          currency={currency}
          onEditClick={
            permissions.CreateAutoBalanceTransfer
              ? () => history.push(`/auto-balance-transfer/update/${currency}`, { isCurrencyNamed })
              : undefined
          }
          onCancelClick={
            permissions.CreateAutoBalanceTransfer ? () => cancelTransfer(currency, isCurrencyNamed) : undefined
          }
        />
      );
    }

    // A schedule may exist that could not be loaded, so don't offer to set up another.
    if (currencyData?.[currency]?.isTransferUnknown) {
      return (
        <BodyText>
          {t('transferUnknown', {
            defaultValue: 'Could not load your auto balance transfer. Reload the page to try again.',
          })}
        </BodyText>
      );
    }

    // Operational Accounts is closed to client-accounts users, who pay out to the primary client account instead.
    const canSetUp = permissions.CreateAutoBalanceTransfer && (hasOperationalAccount || !clientAccountsEnabled);

    return (
      <NoTransferCard
        currency={isCurrencyNamed ? currency : undefined}
        hasOperationalAccount={hasOperationalAccount}
        onSetUpClick={
          canSetUp
            ? () =>
                hasOperationalAccount
                  ? history.push(`/auto-balance-transfer/set-up/${currency}`, { isCurrencyNamed })
                  : history.push('/operational-accounts')
            : undefined
        }
      />
    );
  };

  return (
    <Box flx="col" gap="lg">
      <Title>{t('title', { defaultValue: 'Auto Balance Transfer' })}</Title>

      {lastCancelledCurrency && (
        <MessageBox colorScheme="success">
          <BodyText size="sm">
            {currencies.length > 1
              ? t('cancelSuccessCurrency', {
                  defaultValue: 'Your automated {{currency}} balance transfer was cancelled.',
                  currency: lastCancelledCurrency,
                })
              : t('cancelSuccess', { defaultValue: 'Your automated balance transfer was cancelled.' })}
          </BodyText>
        </MessageBox>
      )}

      {currencyData ? (
        <CurrencyTabs
          currencies={currencies}
          activeCurrency={activeCurrency}
          onActiveCurrencyChange={setActiveCurrency}
          renderCurrency={renderCurrency}
        />
      ) : (
        <Skeleton height={420} borderRadius={10} />
      )}
    </Box>
  );
}
